import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { navGroups } from './supportNav.js';

// Shared navigation state for the Support Panel shell + dashboard, so the two
// never drift. `route()` resolves via the global Ziggy helper (window.route in
// the browser via @routes, globalThis.route under SSR), matching the rest of the
// app — no import needed.
export function useSupportNav() {
    const page = usePage();
    const auth = computed(() => page.props.auth);

    // Visibility mirrors the previous layout's per-link checks exactly:
    // admin tools were gated on email === 'admin@admin.com'; the owner-only
    // LE Export was gated on id === 1. Kept identical so no tool becomes
    // visible (and then controller-rejected) that wasn't before.
    const isAdmin = computed(() => auth.value?.user?.email === 'admin@admin.com');
    const isOwner = computed(() => auth.value?.user?.id === 1);

    const can = (visibility) =>
        visibility === 'all' ||
        (visibility === 'admin' && isAdmin.value) ||
        (visibility === 'owner' && isOwner.value);

    // Items inherit their group's visibility unless they declare their own (e.g. an admin-only
    // tool inside the General group). Groups left with no visible items are dropped, so a plain
    // support user sees only "General".
    const visibleGroups = computed(() =>
        navGroups
            .filter((g) => can(g.visibility))
            .map((g) => ({ ...g, items: g.items.filter((i) => can(i.visibility ?? g.visibility)) }))
            .filter((g) => g.items.length > 0)
    );

    // SSR-safe active detection: compare normalized pathnames from Inertia's
    // page.url. ziggy's route().current() is unreliable during server render.
    const normalizePath = (p) => (p || '/').split('?')[0].replace(/\/+$/, '') || '/';
    const currentPath = computed(() => normalizePath(page.url));

    const hrefFor = (routeName) => {
        try {
            return normalizePath(new URL(route(routeName), 'http://x').pathname);
        } catch (e) {
            // A missing/renamed route must never crash the (server) render.
            return '';
        }
    };
    const isActive = (routeName) => {
        const target = hrefFor(routeName);
        return !!target && currentPath.value === target;
    };

    return { navGroups, visibleGroups, auth, isAdmin, isOwner, currentPath, isActive };
}
